import { useGetAdminUsersQuery } from "@/store/api";

/** Admin-only "filter by user" dropdown. Value semantics:
 *    null  = "All users"
 *    "unassigned" = rows where user_id IS NULL (legacy/system rows)
 *    number = specific user id
 *
 *  Use {@link useUserEmailById} to render `#uid email` cells from a
 *  user_id without each row firing its own query. */
export type UserFilterValue = number | "unassigned" | null;

export function UserFilter({
  value, onChange, includeUnassigned = false,
}: {
  value: UserFilterValue;
  onChange: (v: UserFilterValue) => void;
  includeUnassigned?: boolean;
}) {
  const users = useGetAdminUsersQuery();
  const opts = (users.data ?? []).slice().sort((a, b) => {
    // Admin first, then by email.
    if (a.role === "admin" && b.role !== "admin") return -1;
    if (a.role !== "admin" && b.role === "admin") return 1;
    return (a.email || "").localeCompare(b.email || "");
  });
  const current =
    value === null ? "" :
    value === "unassigned" ? "__unassigned__" :
    String(value);
  return (
    <select
      value={current}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "") onChange(null);
        else if (v === "__unassigned__") onChange("unassigned");
        else onChange(Number(v));
      }}
      title="Filter by user"
      className="h-8 text-xs rounded-md border border-border bg-bg2 text-fg px-2"
    >
      <option value="">All users</option>
      {includeUnassigned && (
        <option value="__unassigned__">(no user_id)</option>
      )}
      {opts.map((u) => (
        <option key={u.id} value={u.id}>
          #{u.id} {u.email}{u.role === "admin" ? " (admin)" : ""}
        </option>
      ))}
    </select>
  );
}

/** Returns a Map<user_id, email> built from the admin users list.
 *  Useful for resolving `user_id` columns to email labels without
 *  making each row component fetch the list itself. */
export function useUserEmailById(): Map<number, string> {
  const users = useGetAdminUsersQuery();
  const m = new Map<number, string>();
  for (const u of users.data ?? []) m.set(u.id, u.email);
  return m;
}

/** Apply a UserFilter value to a row predicate. Pass any row with a
 *  `user_id?: number | null` field and this returns true iff it passes
 *  the current filter. */
export function matchesUserFilter(
  row: { user_id?: number | null | undefined },
  value: UserFilterValue,
): boolean {
  if (value === null) return true;
  if (value === "unassigned") return row.user_id == null;
  return row.user_id === value;
}
